/**
 * Item Classification Service
 *
 * DB read/write for item_classification_proposals and the
 * classification_status column on items.
 *
 * Every write goes through an RPC per Build Contract §2.3a — category and
 * altitude are comparability-sensitive. Proposing (772), accepting (735),
 * partially accepting (756), setting directly and rejecting (756/759) each
 * have exactly one server function, and the table itself refuses direct
 * client writes.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  rowToClassificationProposal,
  type ItemClassificationProposal,
} from '../types/prioritization/itemClassification';

const TABLE = 'item_classification_proposals';

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Fetch the single active proposal for an item, or null if none.
 */
export async function getActiveProposal(itemId: string): Promise<{
  data: ItemClassificationProposal | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('item_id', itemId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: null };

    return { data: rowToClassificationProposal(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Every open proposal one agent made in a workspace, newest first — the
 * agent report page's "waiting for a person" list (spec §5).
 */
export async function listOpenAgentProposals(workspaceId: string, agentName: string): Promise<{
  data: ItemClassificationProposal[];
  error: string | null;
}> {
  try {
    const { data, error } = await getSupabaseClient()
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('proposed_by_agent', agentName)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(500);
    if (error) return { data: [], error: error.message };
    return { data: ((data ?? []) as Record<string, unknown>[]).map(rowToClassificationProposal), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Every classification row in a workspace (newest first) — what the item list
 * needs to mark who decided each item's category and altitude (lib/valueMark).
 * One row per determination event, so this stays small.
 */
export async function listWorkspaceClassificationHistory(workspaceId: string): Promise<{
  data: ItemClassificationProposal[];
  error: string | null;
}> {
  try {
    const { data, error } = await getSupabaseClient()
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: ((data ?? []) as Record<string, unknown>[]).map(rowToClassificationProposal), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Fetch the full proposal history for an item (newest first).
 */
export async function getProposalHistory(itemId: string): Promise<{
  data: ItemClassificationProposal[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('item_id', itemId)
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };

    return {
      data: (data ?? []).map((row) =>
        rowToClassificationProposal(row as Record<string, unknown>),
      ),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export interface CreateAgentProposalInput {
  /**
   * Kept for callers' own bookkeeping — not sent. The server takes the
   * workspace from the item row, so a client cannot file a proposal into a
   * workspace the item does not belong to.
   */
  workspaceId: string;
  itemId: string;
  agentName: string; // e.g. 'atc'
  classificationFields: {
    category: string;
    altitudeLevel: number;
    executionProfile?: string;
    questionResponses?: unknown[];
    rationale?: string;
    confidence?: 'high' | 'medium' | 'low';
  };
}

/**
 * Record an agent classification proposal — `propose_item_classification`
 * (migration 772). One transaction: any existing active proposal for the item
 * is superseded, the new one is recorded, and the item moves to
 * classification_status 'proposed'. The old client did those three as separate
 * round trips, so any one of them could land without the others (two active
 * proposals for one item, or an item stuck on 'empty' with a live proposal).
 *
 * Does NOT set items.item_category or altitude_level — those only change when
 * a person accepts or sets them directly.
 */
export async function createAgentProposal(input: CreateAgentProposalInput): Promise<{
  data: ItemClassificationProposal | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();

    // snake_case keys to match the DB convention for classification_fields.
    const classificationFields = {
      category: input.classificationFields.category,
      altitude_level: input.classificationFields.altitudeLevel,
      execution_profile: input.classificationFields.executionProfile ?? null,
      question_responses: input.classificationFields.questionResponses ?? [],
      rationale: input.classificationFields.rationale ?? null,
      confidence: input.classificationFields.confidence ?? null,
    };

    const { data, error } = await supabase.rpc('propose_item_classification', {
      p_item_id: input.itemId,
      p_agent_name: input.agentName,
      p_classification_fields: classificationFields,
    });

    if (error) return { data: null, error: error.message };

    const result = (data ?? {}) as {
      success?: boolean;
      error?: string;
      proposal?: Record<string, unknown>;
    };
    if (result.success !== true || !result.proposal) {
      return { data: null, error: result.error ?? 'The proposal could not be recorded.' };
    }

    return { data: rowToClassificationProposal(result.proposal), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Accept an active proposal — calls the `accept_classification_proposal` RPC.
 * This is an authority transition (comparability-sensitive) per §2.3a.
 */
export async function acceptProposal(
  proposalId: string,
  acceptedByUserId: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('accept_classification_proposal', {
      p_proposal_id: proposalId,
      p_accepted_by: acceptedByUserId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string };
    return { success: result.success, error: result.error };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Partially accept an active proposal — calls the `partial_accept_classification_proposal` RPC.
 *
 * Allows humans to accept just the category, just the altitude, or both.
 * Passing neither as true is equivalent to rejection.
 *
 * Authority transition per §2.3a.
 */
export async function partialAcceptProposal(
  proposalId: string,
  acceptedByUserId: string,
  acceptCategory: boolean,
  acceptAltitude: boolean,
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('partial_accept_classification_proposal', {
      p_proposal_id:     proposalId,
      p_accepted_by:     acceptedByUserId,
      p_accept_category: acceptCategory,
      p_accept_altitude: acceptAltitude,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string };
    return { success: result.success, error: result.error };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface SetDirectClassificationInput {
  itemId: string;
  category: string;
  altitudeLevel: number;
  userId: string;
  questionResponses?: unknown[];
  rationale?: string;
}

/**
 * Direct human classification — calls the `set_item_classification` RPC.
 * Bypasses proposed state; sets classification_status = 'approved' immediately.
 * Authority transition per §2.3a.
 */
export async function setDirectClassification(
  input: SetDirectClassificationInput,
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = getSupabaseClient();
    const classificationFields = {
      category: input.category,
      altitude_level: input.altitudeLevel,
      question_responses: input.questionResponses ?? [],
      rationale: input.rationale ?? null,
      source: 'user_direct',
    };

    const { data, error } = await supabase.rpc('set_item_classification', {
      p_item_id: input.itemId,
      p_category: input.category,
      p_altitude_level: input.altitudeLevel,
      p_user_id: input.userId,
      p_classification_fields: classificationFields,
    });

    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string };
    return { success: result.success, error: result.error };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Reject an active proposal without setting a new classification — calls the
 * `reject_classification_proposal` RPC (migration 756). One transaction: the
 * proposal becomes `rejected`, the item goes back to `empty` when no other
 * active proposal remains, and a CLASSIFICATION_PROPOSAL_REJECTED governance
 * event is recorded. Authority mirrors acceptance: a member of the proposal's
 * workspace. Authority transition per §2.3a.
 */
export async function rejectProposal(
  proposalId: string,
  reason?: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('reject_classification_proposal', {
      p_proposal_id: proposalId,
      p_reason:      reason?.trim() || null,
    });

    if (error) return { success: false, error: error.message };

    const result = (data ?? {}) as { success?: boolean; error?: string };
    return { success: result.success === true, error: result.error };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
